import { ukDate } from "../lib/dates";
import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { mediaUrl, formatApiError } from "../lib/api";
import { undoableDelete } from "../lib/undo";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { ChevronLeft, ChevronRight, Plus, MapPin, Clock, CalendarDays, ImageIcon } from "lucide-react";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const inputCls = "w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 focus:border-[#0B5E4E] outline-none transition-all px-4 py-2.5 text-sm";
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const CAT_COLOR = { Service: "#0B5E4E", Prayer: "#17A88A", Youth: "#084438", Meeting: "#A8A29E", Outreach: "#79C0AE", Task: "#14211F" };

export default function Calendar() {
  const { user } = useAuth();
  const isStaff = !!user?.caps?.manage_calendar;
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", event_date: "", start_time: "", location: "", category: "Service", description: "" });
  const [selected, setSelected] = useState(null);
  const [coverUploading, setCoverUploading] = useState(null);
  const coverRefs = useRef({});

  const load = async () => {
    const { data } = await api.get("/calendar");
    setItems(data);
    return data;
  };
  useEffect(() => { load(); }, []);

  const uploadCover = async (eventId, file) => {
    if (!file) return;
    setCoverUploading(eventId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/events/${eventId}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Cover updated");
      const data = await load();
      setSelected((prev) => prev ? { ...prev, items: data.filter((i) => i.date === prev.date) } : prev);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCoverUploading(null); }
  };

  const byDate = useMemo(() => {
    const map = {};
    items.forEach((it) => { if (!it.date) return; (map[it.date] = map[it.date] || []).push(it); });
    return map;
  }, [items]);

  const grid = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1);
    const startDow = first.getDay();
    const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  }, [cursor]);

  const dateStr = (d) => `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const todayStr = new Date().toISOString().slice(0, 10);
  const move = (dir) => { let m = cursor.m + dir, y = cursor.y; if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; } setCursor({ y, m }); };

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/events", form); toast.success("Event added"); setOpen(false); setForm({ title: "", event_date: "", start_time: "", location: "", category: "Service", description: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delEvent = (id) => { const ev = items.find((x) => x.id === id); setSelected(null); setItems((l) => l.filter((x) => x.id !== id)); undoableDelete({ label: ev?.title || "Event", entityType: "event", perform: () => api.delete(`/events/${id}`), onUndo: load }); };

  const upcoming = [...items].filter((i) => i.date >= todayStr).sort((a, b) => a.date.localeCompare(b.date)).slice(0, 6);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="sj-page-hero flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Calendar</h1>
          <p style={{ color: "var(--sj-text-2)" }}>Events, services & team task deadlines</p>
        </div>
        {isStaff && (
        <button data-testid="add-event-button" onClick={() => { setForm({ ...form, event_date: todayStr }); setOpen(true); }} className="flex items-center gap-2 rounded-full px-5 py-2.5 text-white font-medium shadow-sm hover:brightness-105 transition-all" style={{ background: "var(--sj-primary)" }}>
          <Plus className="h-5 w-5" /> Add Event
        </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={`${CARD} p-6 lg:col-span-2`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-head text-xl font-semibold">{MONTHS[cursor.m]} {cursor.y}</h2>
            <div className="flex gap-2">
              <button data-testid="cal-prev" onClick={() => move(-1)} className="p-2 rounded-lg border hover:bg-[#EDF2F4]" style={{ borderColor: "var(--sj-border)" }}><ChevronLeft className="h-4 w-4" /></button>
              <button data-testid="cal-next" onClick={() => move(1)} className="p-2 rounded-lg border hover:bg-[#EDF2F4]" style={{ borderColor: "var(--sj-border)" }}><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1.5 mb-2">
            {DOW.map((d) => <div key={d} className="text-center text-xs font-semibold uppercase tracking-wider py-1" style={{ color: "var(--sj-text-3)" }}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {grid.map((d, i) => {
              if (!d) return <div key={`pad-${i}`} />;
              const ds = dateStr(d);
              const dayItems = byDate[ds] || [];
              const isToday = ds === todayStr;
              return (
                <button key={ds} data-testid={`cal-day-${d}`} onClick={() => dayItems.length && setSelected({ date: ds, items: dayItems })}
                  className="aspect-square rounded-xl p-1.5 text-left flex flex-col border transition-all hover:border-[#0B5E4E]"
                  style={{ borderColor: isToday ? "var(--sj-primary)" : "var(--sj-border)", background: isToday ? "var(--sj-peach)" : "#fff" }}>
                  <span className="text-xs font-semibold" style={{ color: isToday ? "var(--sj-primary)" : "var(--sj-text-2)" }}>{d}</span>
                  <div className="flex flex-wrap gap-0.5 mt-auto">
                    {dayItems.slice(0, 3).map((it) => <span key={`${it.type}-${it.id}`} className="h-1.5 w-1.5 rounded-full" style={{ background: CAT_COLOR[it.category] || "#56626A" }} />)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className={`${CARD} p-6`}>
          <h3 className="font-head text-xl font-semibold mb-4">Upcoming</h3>
          <div className="space-y-3" data-testid="upcoming-list">
            {upcoming.length === 0 && <div className="text-sm" style={{ color: "var(--sj-text-3)" }}>Nothing coming up.</div>}
            {upcoming.map((it) => (
              <div key={`${it.type}-${it.id}`} className="flex items-start gap-3 p-3 rounded-xl" style={{ background: "var(--sj-bg)" }}>
                {it.type === "event" && it.cover_file_id ? (
                  <img src={mediaUrl(it.cover_file_id)} alt={it.title} className="h-9 w-9 rounded-lg object-cover shrink-0" />
                ) : (
                <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${CAT_COLOR[it.category] || "#56626A"}1A` }}>
                  <CalendarDays className="h-4.5 w-4.5" style={{ color: CAT_COLOR[it.category] || "#56626A" }} />
                </div>
                )}
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{it.title}</div>
                  <div className="text-xs flex items-center gap-2 flex-wrap" style={{ color: "var(--sj-text-2)" }}>
                    <span>{ukDate(it.date)}</span>
                    {it.start_time && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{it.start_time}</span>}
                    {it.type === "task" && <span className="uppercase tracking-wide" style={{ color: CAT_COLOR.Task }}>task</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Day detail */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head text-2xl">{ukDate(selected?.date)}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            {selected?.items.map((it) => (
              <div key={`${it.type}-${it.id}`} className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--sj-border)" }}>
                {it.type === "event" && it.cover_file_id && (
                  <img src={mediaUrl(it.cover_file_id)} alt={it.title} className="w-full h-40 object-cover" data-testid={`event-cover-img-${it.id}`} />
                )}
                <div className="p-4 flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: CAT_COLOR[it.category] || "#56626A" }} />
                      <span className="font-semibold">{it.title}</span>
                    </div>
                    <div className="text-xs mt-1 flex gap-3" style={{ color: "var(--sj-text-2)" }}>
                      {it.start_time && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{it.start_time}</span>}
                      {it.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{it.location}</span>}
                      <span>{it.category}</span>
                    </div>
                  </div>
                  {it.type === "event" && (
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      {isStaff && (
                        <>
                          <input ref={(el) => (coverRefs.current[it.id] = el)} type="file" accept="image/*" className="hidden"
                            data-testid={`event-cover-input-${it.id}`} onChange={(e) => uploadCover(it.id, e.target.files?.[0])} />
                          <button data-testid={`event-cover-button-${it.id}`} disabled={coverUploading === it.id} onClick={() => coverRefs.current[it.id]?.click()}
                            className="flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border hover:bg-[#EDF2F4] disabled:opacity-60" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
                            <ImageIcon className="h-3.5 w-3.5" /> {coverUploading === it.id ? "Uploading..." : (it.cover_file_id ? "Change cover" : "Add cover")}
                          </button>
                        </>
                      )}
                      <button data-testid={`event-delete-${it.id}`} onClick={() => delEvent(it.id)} className="text-xs font-medium rounded-full px-3 py-1.5 border hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-primary)" }}>Delete</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create event */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head text-2xl">Add Event</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4 mt-2">
            <input data-testid="event-title-input" required placeholder="Event title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
            <div className="grid grid-cols-2 gap-4">
              <input data-testid="event-date-input" required type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className={inputCls} />
              <input data-testid="event-time-input" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <input placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputCls} />
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                <option>Service</option><option>Prayer</option><option>Youth</option><option>Meeting</option><option>Outreach</option>
              </select>
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
              <button type="submit" data-testid="save-event-button" className="rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105 transition-all" style={{ background: "var(--sj-primary)" }}>Add Event</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
